import React, { useState, useCallback, Suspense, lazy } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatCertDays } from '../../lib/cert-days';
import { api } from '../../api/client';
import { formatCalendarDate, formatInstantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtQty } from '../../lib/display-labels';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Loader2, CheckCircle, AlertTriangle, XCircle, Printer, Download, Mail, Camera, X, Wrench } from 'lucide-react';
import { toast } from 'sonner';

import { inspectionsListPath, isFieldSurface } from '../../lib/inspection-surface';
import { inspectionFormTypeLabel } from '../../lib/inspection-labels';

// Lazy: the repair dialog pulls in the photo uploader and its upload queue,
// which this read-only page otherwise never needs — loaded on the click.
const MaintenanceRecordDialog = lazy(() => import('../../components/maintenance/MaintenanceRecordDialog'));

/**
 * The page title band, to CLAUDE.md's "Page Layout Gold Standard": one line,
 * plain arrow, no "Back" word, `text-2xl` title, actions right, not sticky.
 *
 * WHY THIS IS LOCAL AND NOT `BackHeader`
 * --------------------------------------
 * `BackHeader` renders the deprecated two-line pattern — a full-width "Back"
 * button row above a `text-base` title. It is still correct for the pages that
 * use it and is deliberately left untouched: CLAUDE.md's rule is that the
 * legacy header gets swept on purpose or not at all, never as drive-by work.
 * This file is the scoped conversion; nothing else moves with it.
 *
 * The back target stays an explicit records list rather than `navigate(-1)`.
 * This page is deep-linkable — the PDF and the emailed summary both point at a
 * single inspection — so a visitor can arrive with no history behind them, and
 * `-1` would leave them where they started. `location.state` is carried through
 * so the list's tab and filters survive the round trip, as BackHeader did.
 *
 * WHICH list it is now depends on the route tree, because this page is
 * registered in two of them. A hardcoded `/safety/records` is only a route in
 * the office tree; on the casing field surface it resolves through that tree's
 * catch-all and lands the user on /field. That is the same defect the form's
 * post-submit hop had, so both resolve through one function —
 * lib/inspection-surface.ts — rather than each carrying its own literal.
 *
 * BackHeader's subtitle (date · operator) is dropped rather than stacked back
 * under the title: both values are already rendered in General Information
 * below, so no fact leaves the page.
 */
function InspectionHeader({ title, actions }: { title: string; actions?: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="border-b border-border px-6 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => navigate(inspectionsListPath(location.pathname), { state: location.state })}
            aria-label="Back to Inspections"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-foreground truncate">{title}</h1>
        </div>
        {actions && <div className="flex items-center gap-3 shrink-0">{actions}</div>}
      </div>
    </div>
  );
}


/**
 * What `tong_make` / `tong_model` actually hold, per form type.
 *
 * The columns were named for the Power Tong inspection and every later type
 * reused them, so all six rendered "Tong Make". Only Power Tong is a tong:
 * tong_truck holds a truck (Dodge, serial T04) and CRTi / elevators / slips
 * hold their own tool. Kept in step with MAKE_MODEL_LABELS in the API's
 * inspection-templates.routes.ts — the PDF and this screen must read the same,
 * and they are the two renderings of one signed record.
 */
const MAKE_MODEL_LABELS: Record<string, { make: string; model: string }> = {
  tong_truck: { make: 'Truck Make', model: 'Truck Model' },
  power_tong: { make: 'Tong Make / Manufacturer', model: 'Tong Model' },
};

const makeModelLabels = (formType: string) =>
  MAKE_MODEL_LABELS[formType] ?? { make: 'Make / Manufacturer', model: 'Model' };

/**
 * The tool's CAODC certification budget, as "150 of 250 working days".
 *
 * Read off the equipment record the API joins in — cert_day_limit against
 * working_days_since_inspection, which cert-day.service increments from
 * ticket_equipment_usage at office approval. Surfacing that here is why there
 * is no calendar "next inspection due" date: a second interval system would be
 * two answers to one question.
 *
 * THE ONE SURFACE THAT SAYS REMAINING RATHER THAN USED. Every cert-day counter
 * in the app now renders through lib/cert-days.ts, and every other one says days
 * USED because that is the server's vocabulary and the direction its progress
 * bars fill. This row keeps remaining for two reasons: its own label already
 * says "Cert days remaining", so the sentence is not ambiguous the way a bare
 * bar caption is; and the API prints the identical string into this record's PDF
 * from inspection-templates.routes.ts, so converting only the screen would put
 * one number on glass and a different one on paper.
 *
 * IT IS THE TOOL'S CYCLE, NOT THIS INSPECTION'S — nothing resets the counter,
 * so a tool inspected this morning still shows its accumulated total. Hence
 * "Cert days remaining" rather than a next-due date.
 *
 * '—' whenever the figure cannot be produced: no equipment linked, no equipment
 * row, or no limit set on the tool. That is most records today, and a dash
 * makes the gap visible instead of implying a budget nobody configured.
 */
const TONG_TRUCK_FORM_TYPE = 'tong_truck';

const certDaysValue = (s: any): string =>
  formatCertDays(
    { daysUsed: s?.cert_days_used, dayLimit: s?.cert_day_limit, daysRemaining: s?.cert_days_remaining },
    'remaining',
  );

/**
 * Which form types actually capture the four header fields 0305 gave columns to.
 *
 * A row appears only for a form type whose builder has the input. Showing
 * "Rig Name / Number: —" on a Power Tong report would not be an absent value, it
 * would be a field that form has never had — the same distinction the Cert-days
 * row draws for Tong Truck.
 *
 * `well` covers three types, not one. The elevators and slip_elevator_l1
 * builders in EquipmentInspectionForm.tsx have always posted `well_name` too,
 * and 0305 maps the key without looking at form_type, so those now land as well.
 * Both have zero submissions in production today; leaving them off this list
 * would make the first one silently drop its well name once somebody files one.
 *
 * `rig` WIDENED IN 0309. The other three builders capture a rig too; they used
 * to post it under the dead key `rig`, which is why 0305 could not map it and
 * left the row off this list. The handler now reads either key into the one
 * rig_name column and this form posts `rig_name`, so all four types have a rig
 * name to print.
 *
 * MIRRORED in the API's src/routes/inspection-templates.routes.ts, alongside
 * MAKE_MODEL_LABELS. The PDF and this screen are two renderings of one signed
 * record and must show the same field set — the parity rule from #260. Change
 * one, change both.
 *
 * THIS IS THE LAST SUCH MIRROR. The 0309 fields ship their own scoping in the
 * response — form_detail_fields, shows_deficiencies, shows_supervisor_signature
 * — precisely because a list that must be edited in two repos drifts silently,
 * and a field quietly present on one surface and missing on the other is the
 * bug both PRs exist to close. Do not add to this record; put new scoping in
 * withRenderScoping on the API instead.
 */
const CRTI_FORM_TYPE = 'crti';
const HEADER_FIELD_FORM_TYPES: Record<'rig' | 'well' | 'meterage', readonly string[]> = {
  rig: [CRTI_FORM_TYPE, 'elevators', 'slip_elevator_l1', 'rotary_slips_l1'],
  well: [CRTI_FORM_TYPE, 'elevators', 'slip_elevator_l1'],
  meterage: [CRTI_FORM_TYPE],
};

/**
 * The 0305 header rows, in the order the CRTi form asks for them.
 *
 * Meterage goes through `fmtQty`, which already gets the part that matters:
 * NULL renders '—' and a real 0 renders '0'. On a wear-tracking figure those are
 * different facts — "nobody recorded it" against "this tool has run nothing
 * since its last inspection" — and 0305 went to some trouble to keep them apart
 * in the database. A `|| '—'` here would put them straight back together.
 *
 * NUMERIC(12,2) arrives as the string '1450.00'; fmtQty's round trip through
 * Number is also what strips the stored scale back to what was typed.
 */
const crtiHeaderFields = (s: any): { label: string; value: string }[] => {
  const formType = s?.form_type;
  const fields: { label: string; value: string }[] = [];
  if (HEADER_FIELD_FORM_TYPES.rig.includes(formType)) {
    fields.push({ label: 'Rig Name / Number', value: s.rig_name || '—' });
  }
  if (HEADER_FIELD_FORM_TYPES.well.includes(formType)) {
    fields.push({ label: 'Well Name', value: s.well_name || '—' });
  }
  if (HEADER_FIELD_FORM_TYPES.meterage.includes(formType)) {
    fields.push(
      { label: 'Meterage Run Since Last Inspection', value: fmtQty(s.meterage_since_last_inspection) },
      { label: 'Total Lifetime Meterage', value: fmtQty(s.total_lifetime_meterage) },
    );
  }
  return fields;
};

/**
 * The `uploads.entity_type` inspection signatures file under (0309).
 *
 * DISTINCT FROM 'inspections' ON PURPOSE. The photos query below is
 * `listForEntity('inspections', id)` and the endpoint filters on that string
 * verbatim, so a signature stored under it would render as a tile in the Photos
 * grid — here and in the PDF, beside the signature block it duplicates.
 */
const SIGNATURE_ENTITY_TYPE = 'inspection_signature';

/**
 * A stored signature, resolved to something an <img> can render.
 *
 * TWO FORMATS ARE LIVE AT ONCE, AND THAT IS THE NORMAL STATE, not a window to
 * wait out. The column held inline base64 for its first 27 rows; 0309 moves it
 * to R2 keys, and the tail migration converts those rows ONE AT A TIME, on
 * purpose — it must never clear a row's base64 until it has read the uploaded
 * object back, because a row left un-migrated is harmless and a row migrated
 * wrongly is a lost signature on a signed safety record. So a partially
 * migrated table is the expected condition and both arms have to work on the
 * same screen, in the same session.
 *
 *   data:…   inline base64, pre-0309 rows and any the tail has not reached
 *   http…    already an absolute URL
 *   bare key an R2 object key — resolved through the uploads listing, which
 *            carries a fresh presigned URL for each row
 *
 * WHY THE LISTING AND NOT THE KEY DIRECTLY. A presigned URL cannot be built
 * client-side and must never be persisted (they expire — CLAUDE.md, broken
 * twice). The listing mints one per row per fetch, and React Query re-runs it
 * on mount, so what the <img> gets is always freshly signed. This matches the
 * Photos grid on this same page, which reads its URLs from the same endpoint.
 *
 * WHY MATCHING ON THE KEY MATTERS. An elevators record has TWO signatures under
 * one (entity_type, entity_id) pair, so the listing alone cannot say which row
 * is the inspector's and which the supervisor's. The column value is the
 * discriminator: each holds the exact s3Key its own upload produced. Migrated
 * rows resolve the same way — the tail migration writes an uploads row too.
 */
const resolveSignature = (
  value: unknown,
  signatureUploads: any[],
): { src: string | null; unresolvedKey: boolean } => {
  if (typeof value !== 'string' || !value.trim()) return { src: null, unresolvedKey: false };

  const raw = value.trim();
  if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) {
    return { src: raw, unresolvedKey: false };
  }

  // camelCase on reads, but tolerate both — CLAUDE.md's serialization rule.
  const match = signatureUploads.find(
    (u: any) => (u?.s3Key ?? u?.s3_key ?? u?.key) === raw,
  );
  const src = match?.url ?? match?.presigned_url ?? null;
  return { src: src ?? null, unresolvedKey: !src };
};

/**
 * Severity for display, on an EXHAUSTIVE map keyed on every value the API's
 * SEVERITY_VALUES admits — low | medium | high | critical — plus the null case.
 *
 * `high` is on this list even though the form's selector only offers three.
 * The normalizer stores an unfamiliar-but-recognised level rather than folding
 * it to null, so a renderer that handled only the three it knew about would
 * print a blank for a real assessment. A binary or best-guess fallback here is
 * the exact defect CLAUDE.md's label-map rule names.
 *
 * ABSENT IS '—', NEVER 'LOW'. The agent path files deficiencies with no
 * severity at all, and a guessed 'low' on a critical defect is indistinguishable
 * from an assessment somebody actually made. Mirrors severityLabel and the PDF's
 * colour classes in the API's src/lib/inspection-deficiencies.ts.
 */
const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400 print:text-red-700',
  high: 'bg-amber-500/20 text-amber-400 print:text-amber-600',
  medium: 'bg-amber-500/20 text-amber-400 print:text-amber-600',
  low: 'bg-green-500/20 text-green-400 print:text-green-700',
};

const severityLabel = (s: unknown): string =>
  typeof s === 'string' && s.trim() ? s.trim().toUpperCase() : '—';

const severityStyle = (s: unknown): string =>
  (typeof s === 'string' && SEVERITY_STYLES[s.trim().toLowerCase()]) ||
  'bg-fieldiq-border text-fieldiq-muted print:text-gray-500';

const getReportTitle = (formType: string) => {
  switch (formType) {
    case 'power_tong': return 'Hydraulic Power Tong Inspection Report';
    case 'crti': return 'Casing Running Tool Inspection & Maintenance Record';
    case 'elevators': return 'Conventional Casing Elevator Inspection Report';
    case 'slip_elevator_l1': return 'Slip Type Elevator — Level 1 Pre-Use Inspection';
    case 'rotary_slips_l1': return 'Rotary Slips — Level 1 Pre-Use Inspection';
    case 'tong_truck': return 'Tong Truck Inspection Report';
    default: return 'Equipment Inspection Report';
  }
};

const PDF_FILENAME_MAP: Record<string, string> = {
  power_tong: 'PowerTong_Inspection',
  crti: 'CRTi_Inspection',
  elevators: 'Elevator_Inspection',
  slip_elevator_l1: 'SlipElevator_L1_Inspection',
  rotary_slips_l1: 'RotarySlips_L1_Inspection',
  tong_truck: 'TongTruck_Inspection',
};

const RESULT_STYLES: Record<string, string> = {
  pass: 'bg-green-500/20 text-green-400',
  minor: 'bg-amber-500/20 text-amber-400',
  fail: 'bg-red-500/20 text-red-400',
  conditional: 'bg-amber-500/20 text-amber-400',
};

const PRINT_RESULT_COLORS: Record<string, string> = {
  pass: 'print:text-green-700',
  minor: 'print:text-amber-600',
  fail: 'print:text-red-700',
};


function ResultBadge({ result }: { result: string | null }) {
  if (!result) return <span className="text-fieldiq-muted print:text-gray-400 text-sm">—</span>;
  const colors = RESULT_STYLES[result] || 'bg-gray-500/20 text-gray-400';
  const printColor = PRINT_RESULT_COLORS[result] || '';
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold capitalize ${colors} print:bg-transparent print:px-0 print:text-[11px] ${printColor}`}>
      {result}
    </span>
  );
}

function ResultIcon({ result }: { result: string | null }) {
  switch (result) {
    case 'pass':
      return <CheckCircle size={18} className="text-green-400 print:text-green-700" />;
    case 'minor':
      return <AlertTriangle size={18} className="text-amber-400 print:text-amber-600" />;
    case 'fail':
      return <XCircle size={18} className="text-red-400 print:text-red-700" />;
    default:
      return <div className="w-[18px] h-[18px] rounded-full border-2 border-fieldiq-border print:border-gray-300" />;
  }
}

/**
 * One signature on the record, in the four states it can actually be in.
 *
 * THE BLOCK ALWAYS RENDERS once its form type asks for a signature — an unsigned
 * record shows the ruled line the PDF draws, not nothing. #260: a dropped block
 * leaves the reader unable to tell "nobody signed this" from "this form does not
 * ask for a signature", and those are very different facts on a safety record.
 *
 * THE FOURTH STATE IS WHY THIS IS NOT JUST `src ? img : line`. A column holding
 * a key that the uploads listing cannot resolve is NOT an unsigned record, and
 * drawing the empty line for it would state something false about a document
 * somebody signed. The PDF resolves server-side straight from R2 and legitimately
 * treats a failure as blank; here the resolution depends on a second query that
 * can still be in flight or have failed outright, so the two cases are held
 * apart. The field set is identical either way, which is what the parity rule
 * governs.
 */
function SignatureBlock({
  title,
  role,
  name,
  columnValue,
  signatureUploads,
  uploadsLoading,
}: {
  title: string;
  role: string;
  name: string;
  columnValue: unknown;
  signatureUploads: any[];
  uploadsLoading: boolean;
}) {
  const { src, unresolvedKey } = resolveSignature(columnValue, signatureUploads);
  const hasValue = typeof columnValue === 'string' && !!columnValue.trim();

  return (
    <div className="bg-fieldiq-card print:bg-transparent rounded-xl print:rounded-none p-4 print:px-0 print:border-b print:border-gray-200">
      <h3 className="font-semibold mb-3 text-white print:text-black text-base">{title}</h3>

      {src ? (
        <div className="bg-white rounded-lg p-2 inline-block print:p-0">
          <img src={src} alt={title} className="max-h-[120px] print:max-h-[80px]" />
        </div>
      ) : hasValue && uploadsLoading ? (
        <div className="flex items-center gap-2 text-fieldiq-muted print:text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin print:hidden" />
          Loading signature...
        </div>
      ) : unresolvedKey ? (
        <div className="flex items-start gap-2 text-sm text-amber-300 print:text-amber-700">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>
            A signature is on file for this inspection but could not be loaded. It has not been
            lost — the record still holds it. Reload the page, or open the PDF, which resolves
            signatures server-side.
          </span>
        </div>
      ) : (
        <div className="w-[200px] h-[30px] border-b border-fieldiq-border print:border-black" />
      )}

      <div className="mt-3 text-sm">
        <div className="text-white print:text-black">{name || '—'}</div>
        <div className="text-fieldiq-muted print:text-gray-500">{role}</div>
      </div>
    </div>
  );
}

/**
 * SPLIT, not a swap — the single call site was
 * `fmtDate(submission.inspection_date || submission.created_at)`, which feeds
 * this helper BOTH kinds of value. Same shape as fmtDate/fmtInstantDay in
 * src/pages/tickets/TicketDetail.tsx.
 *
 * `inspection_date` is a CALENDAR DAY: '2026-07-14' parses as UTC midnight,
 * which is 18:00 on the 13th in Alberta, so the inspection read a day early.
 */
function fmtDate(d: string | null | undefined) {
  return formatCalendarDate(d, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';
}

/**
 * The fallback arm. `created_at` is a genuine INSTANT, so it is converted, not
 * prefix-read: an inspection submitted at 21:30 MDT on Jul 14 arrives as
 * '2026-07-15T03:30:00Z', and only converting gets "Jul 14" — the calendar-day
 * path would read the UTC prefix and say Jul 15. Converted into the TENANT's
 * zone (`companies.time_zone`), not the viewer's device.
 */
const fmtInstantDay = (d: string | null | undefined, timeZone: string | null) =>
  formatInstantDay(d, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';

/**
 * One checklist item as the API labels it (api#924, `inspection_items` on both
 * submission GETs). Snake_case, like the rest of this surface.
 *
 * `label_source` is where the label came from, and only one value is a guess:
 *   - `snapshot`         the template as it stood when the crew submitted
 *   - `inline`           the item carried its own label (Tong Truck)
 *   - `current_template` TODAY's form, for a submission made before snapshots
 *                        existed. The form may have changed since, so the
 *                        screen says so rather than presenting it as what the
 *                        crew was shown.
 *   - `none`             no label anywhere. `label` is null and renders `—`;
 *                        never "Item 3", which reads as a name the form gave it.
 *
 * This screen used to label items itself, from `template_sections` — today's
 * template joined onto every submission, old or new — and fell back to
 * `Item N`. That was the current-form guess with nothing marking it as one.
 * The API's resolver is the one labelling rule now; nothing here re-derives it.
 */
interface InspectionItem {
  position: string;
  section_number: number;
  item_index: number;
  section: string | null;
  label: string | null;
  label_source: 'snapshot' | 'inline' | 'current_template' | 'none';
  result: string | null;
  comment: string | null;
}

interface DisplayItem {
  label: string | null;
  result: string | null;
  comment: string | null;
  fromCurrentForm: boolean;
}

interface DisplaySection {
  number: number;
  title: string;
  items: DisplayItem[];
  /** Every item in it was labelled from today's form — say it once, on the heading. */
  fromCurrentForm: boolean;
}

const LABELS_FROM_CURRENT_FORM = '(labels from current form)';
const LABEL_FROM_CURRENT_FORM = '(label from current form)';

function buildDisplaySections(items: InspectionItem[]): DisplaySection[] {
  const grouped = new Map<number, InspectionItem[]>();
  for (const item of items) {
    if (!Number.isFinite(item.section_number)) continue;
    const list = grouped.get(item.section_number) ?? [];
    list.push(item);
    grouped.set(item.section_number, list);
  }

  // The API returns items already in position order; sections are sorted here
  // only because a Map keeps insertion order and that is the API's, not ours.
  return Array.from(grouped.entries())
    .sort(([a], [b]) => a - b)
    .map(([number, group]) => ({
      number,
      // A position heading, not a guessed name: Tong Truck's items carry their
      // own labels but no section title.
      title: group.find(i => i.section)?.section ?? `Section ${number}`,
      fromCurrentForm: group.every(i => i.label_source === 'current_template'),
      items: group.map(i => ({
        label: i.label,
        result: i.result || null,
        comment: i.comment || null,
        fromCurrentForm: i.label_source === 'current_template',
      })),
    }));
}

// ─── Main component ────────────────────────────────────────────────────────

export default function InspectionDetailPage() {
  const timeZone = useTenantTimeZone();
  const { id } = useParams<{ id: string }>();
  const { company } = useAuth();
  const location = useLocation();
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);

  const { data: submission, isLoading, error } = useQuery({
    queryKey: ['inspection-detail', id],
    queryFn: () => api.casingInspections.get(id!).then(r => r.data.data ?? r.data),
    enabled: !!id,
  });

  const { data: photos = [] } = useQuery({
    queryKey: ['inspection-photos', id],
    queryFn: () => api.uploads.listForEntity('inspections', id!).then(r => {
      const payload = r.data?.data ?? r.data ?? [];
      return Array.isArray(payload) ? payload : [];
    }),
    enabled: !!id,
  });

  /**
   * Signatures, fetched separately from the photos above BECAUSE THEY ARE A
   * DIFFERENT entity_type — that separation is the whole point of 0309's
   * 'inspection_signature'. Filing them under 'inspections' would have put them
   * in the query above and drawn them as photo tiles.
   *
   * Runs for every record, not only ones known to hold a key: whether the
   * column holds base64 or a key is a per-row property of how far the tail
   * migration has got, and gating the fetch on it would mean re-deriving that
   * before the data needed to derive it has arrived.
   */
  const { data: signatureUploads = [], isLoading: signaturesLoading } = useQuery({
    queryKey: ['inspection-signatures', id],
    queryFn: () => api.uploads.listForEntity(SIGNATURE_ENTITY_TYPE, id!).then(r => {
      const payload = r.data?.data ?? r.data ?? [];
      return Array.isArray(payload) ? payload : [];
    }),
    enabled: !!id,
  });

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const generatePDF = useCallback(async () => {
    if (!id) return;
    try {
      setDownloadingPDF(true);
      const res = await api.casingInspections.getPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const pdfBase = (submission?.form_type && PDF_FILENAME_MAP[submission.form_type]) || 'Inspection';
      a.download = `${pdfBase}_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPDF(false);
    }
  }, [id]);

  if (isLoading) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark">
        <InspectionHeader title="Inspection Detail" />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
          <span className="ml-2 text-fieldiq-muted text-sm">Loading inspection...</span>
        </div>
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark">
        <InspectionHeader title="Inspection Detail" />
        <div className="px-6 py-12 text-center text-fieldiq-muted">
          <p>Inspection not found or failed to load.</p>
        </div>
      </div>
    );
  }

  const formTypeLabel = inspectionFormTypeLabel(submission);
  const headerLabels = makeModelLabels(submission.form_type);
  const reportTitle = getReportTitle(submission.form_type);
  // The `||` picks the reader, not just the value: a calendar day goes to the
  // calendar-day formatter, the created_at fallback to the instant one.
  const dateStr = submission.inspection_date
    ? fmtDate(submission.inspection_date)
    : fmtInstantDay(submission.created_at, timeZone);
  const equipmentStr = submission.tong_make
    ? `${submission.tong_make}${submission.tong_model ? ` ${submission.tong_model}` : ''}`
    : submission.serial_number || '';
  /**
   * The JOB this inspection was done on — `jobs.job_number`, joined by the API.
   *
   * Distinct from `job_location` directly below it in the grid, which is free
   * text an inspector types and which is NOT a reference to anything. Both are
   * shown because they answer different questions: which job, and where. A
   * screen that showed only the location could not tell you that two
   * inspections belong to the same job.
   */
  const jobStr = submission.job_number
    ? `${submission.job_number}${submission.job_well_name ? ` — ${submission.job_well_name}` : ''}`
    : '';
  const companyName = company?.name || company?.company_name || 'FieldIQ';


  const inspectionItems: InspectionItem[] =
    Array.isArray(submission.inspection_items) ? submission.inspection_items : [];

  const displaySections = buildDisplaySections(inspectionItems);

  /**
   * The 0309 render scoping, TAKEN FROM THE RESPONSE, not re-derived here.
   *
   * `form_detail_fields` arrives already scoped to the form type and already
   * dashed where absent; `shows_deficiencies` and `shows_supervisor_signature`
   * answer the one question the data cannot — an empty list and a form that
   * never asked serialize identically, and telling them apart is the difference
   * between "nothing wrong with this tool" and "we never checked".
   *
   * Mirroring the API's FORM_DETAIL_FIELDS and DEFICIENCY_FORM_TYPES into this
   * file would rebuild exactly the drift 0309 removed. The defaults below are
   * for an API that predates it, not a second source of truth.
   */
  const formDetailFields: { label: string; value: string }[] =
    Array.isArray(submission.form_detail_fields) ? submission.form_detail_fields : [];
  const showsDeficiencies = submission.shows_deficiencies === true;
  const showsSupervisorSignature = submission.shows_supervisor_signature === true;
  const deficiencies: any[] = Array.isArray(submission.deficiencies) ? submission.deficiencies : [];

  const handleEmail = () => {
    const subject = encodeURIComponent(`${formTypeLabel} Inspection — ${dateStr} — ${equipmentStr}`);
    const lines = [
      `${formTypeLabel} Inspection Report`,
      `Company: ${companyName}`,
      `Date: ${dateStr}`,
      `Job: ${jobStr || '—'}`,
      `Equipment: ${equipmentStr || '—'}`,
      `Serial Number: ${submission.serial_number || '—'}`,
      `Inspector: ${submission.inspector_name || submission.operator_name || '—'}`,
      `Overall Result: ${(submission.overall_result || '—').toUpperCase()}`,
      '',
      ...displaySections.flatMap(s => [
        `--- ${s.title}${s.fromCurrentForm ? ` ${LABELS_FROM_CURRENT_FORM}` : ''} ---`,
        ...s.items.map(item =>
          `  ${item.label ?? '—'}${item.fromCurrentForm && !s.fromCurrentForm ? ` ${LABEL_FROM_CURRENT_FORM}` : ''}: ${(item.result || '—').toUpperCase()}${item.comment ? ` (${item.comment})` : ''}`),
        '',
      ]),
      submission.notes ? `Notes: ${submission.notes}` : '',
    ].filter(Boolean);
    const body = encodeURIComponent(lines.join('\n'));
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };

  // "Log repair from this inspection" (api#979). An OFFICE action — the field
  // surface renders this same page and does not get it. Offered only when an
  // item actually FAILED (a minor is a note, not a repair — the API's pre-fill
  // takes failed items only) and the record names an asset to repair.
  const canLogRepair =
    !isFieldSurface(location.pathname) &&
    !!submission.equipment_id &&
    inspectionItems.some((i) => i.result === 'fail');

  const actionButtons = (
    <div className="flex items-center gap-2 no-print">
      {canLogRepair && (
        <button
          onClick={() => setRepairOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg text-sm hover:bg-amber-400 transition-colors"
        >
          <Wrench size={14} /> Log repair from this inspection
        </button>
      )}
      <button
        onClick={() => window.print()}
        className="flex items-center gap-1.5 px-3 py-2 bg-fieldiq-card border border-fieldiq-border text-fieldiq-text rounded-lg text-sm hover:text-white transition-colors"
      >
        <Printer size={14} /> Print
      </button>
      <button
        onClick={generatePDF}
        disabled={downloadingPDF}
        className="flex items-center gap-1.5 px-3 py-2 bg-fieldiq-card border border-fieldiq-border text-fieldiq-text rounded-lg text-sm hover:text-white transition-colors disabled:opacity-50"
      >
        {downloadingPDF ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        {downloadingPDF ? 'Generating...' : 'PDF'}
      </button>
      <button
        onClick={handleEmail}
        className="flex items-center gap-1.5 px-3 py-2 bg-fieldiq-card border border-fieldiq-border text-fieldiq-text rounded-lg text-sm hover:text-white transition-colors"
      >
        <Mail size={14} /> Email
      </button>
    </div>
  );

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark print:bg-white pb-[var(--bottom-chrome)] print:pb-0">
      {/* Print-only header */}
      <div className="hidden print:block px-6 pt-6 pb-4 border-b border-gray-300">
        <h1 className="text-xl font-bold text-black">{reportTitle}</h1>
        <div className="flex justify-between text-sm text-gray-600 mt-1">
          <span>{companyName}</span>
          <span>Date: {dateStr}</span>
          <span>Inspector: {submission.inspector_name || submission.operator_name || '—'}</span>
        </div>
      </div>

      {/* Screen header */}
      <div className="print:hidden">
        <InspectionHeader title={`${formTypeLabel} Inspection`} actions={actionButtons} />
      </div>

      <div className="max-w-[1920px] mx-auto px-6 py-5 print:py-3 space-y-4 print:space-y-3">
        {/* General Information */}
        <div className="bg-fieldiq-card print:bg-transparent rounded-xl print:rounded-none p-4 print:px-0 print:border-b print:border-gray-200">
          <h3 className="font-semibold mb-3 text-white print:text-black text-base">General Information</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <div>
              <span className="text-fieldiq-muted print:text-gray-500 block">{headerLabels.make}</span>
              <span className="text-white print:text-black">{submission.tong_make || '—'}</span>
            </div>
            <div>
              <span className="text-fieldiq-muted print:text-gray-500 block">{headerLabels.model}</span>
              <span className="text-white print:text-black">{submission.tong_model || '—'}</span>
            </div>
            <div>
              <span className="text-fieldiq-muted print:text-gray-500 block">Serial Number</span>
              <span className="text-white print:text-black">{submission.serial_number || '—'}</span>
            </div>
            {/* Unconditional with an em dash, like every sibling in this
                grid and for the reason stated below it: an absent value must
                not remove the row, or "no job recorded" reads as "this form
                has no job field". */}
            <div>
              <span className="text-fieldiq-muted print:text-gray-500 block">Job</span>
              <span className="text-white print:text-black">{jobStr || '—'}</span>
            </div>
            <div>
              <span className="text-fieldiq-muted print:text-gray-500 block">Job Location</span>
              <span className="text-white print:text-black">{submission.job_location || '—'}</span>
            </div>
            <div>
              <span className="text-fieldiq-muted print:text-gray-500 block">Operator</span>
              <span className="text-white print:text-black">{submission.operator_name || '—'}</span>
            </div>
            <div>
              <span className="text-fieldiq-muted print:text-gray-500 block">Date</span>
              <span className="text-white print:text-black">{dateStr}</span>
            </div>
            {/* Rendered unconditionally. These two were behind `&&`, so an
                absent value removed the row entirely and the reader could not
                tell "no inspector recorded" from "this form has no inspector
                field" — the same omission the PDF made, which is how the two
                surfaces came to show different field counts for one record. */}
            <div>
              <span className="text-fieldiq-muted print:text-gray-500 block">Inspector</span>
              <span className="text-white print:text-black">{submission.inspector_name || '—'}</span>
            </div>
            <div>
              <span className="text-fieldiq-muted print:text-gray-500 block">Submitted By</span>
              <span className="text-white print:text-black">{submission.submitted_by_name || '—'}</span>
            </div>
            {/* The four 0305 gave columns to. The CRTi form has always asked for
                them, the handler used to drop them, and this screen has never
                known they exist — so a rig, a well and two wear readings the
                inspector typed were absent from the record they signed. Same
                rows, same order, same values as the PDF's header grid. */}
            {crtiHeaderFields(submission).map(f => (
              <div key={f.label}>
                <span className="text-fieldiq-muted print:text-gray-500 block">{f.label}</span>
                <span className="text-white print:text-black">{f.value}</span>
              </div>
            ))}
            {/* The tool attributes 0309 gave form_details to — elevator_*,
                slip_*, rated_capacity. Already scoped and already dashed by the
                API, in the order that form type asks for them; a CRTi record
                carries none. Same rows, same order, same values as the PDF's
                header grid, because both read one field off one response. */}
            {formDetailFields.map(f => (
              <div key={f.label}>
                <span className="text-fieldiq-muted print:text-gray-500 block">{f.label}</span>
                <span className="text-white print:text-black">{f.value}</span>
              </div>
            ))}
            {/* Structured types only — Tong Truck is a per-shift pre-trip on a
                vehicle, not a certified tool on a working-day budget. Kept in
                step with the same omission in the API's PDF header. */}
            {submission.form_type !== TONG_TRUCK_FORM_TYPE && (
              <div>
                <span className="text-fieldiq-muted print:text-gray-500 block">Cert days remaining</span>
                <span className="text-white print:text-black">{certDaysValue(submission)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Checklist Sections */}
        {displaySections.map(section => (
          <div key={section.number} className="bg-fieldiq-card print:bg-transparent rounded-xl print:rounded-none p-4 print:px-0 print:border-b print:border-gray-200">
            <h3 className="font-semibold mb-3 text-white print:text-black text-base">
              {section.title}
              {section.fromCurrentForm && (
                <span className="ml-2 text-xs font-normal text-fieldiq-muted print:text-gray-500">{LABELS_FROM_CURRENT_FORM}</span>
              )}
            </h3>
            <div className="space-y-2 print:space-y-1">
              {section.items.map((item, idx) => (
                <div key={idx} className="bg-fieldiq-dark print:bg-transparent rounded-lg print:rounded-none p-3 print:py-1.5 print:px-0 print:border-b print:border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white print:text-black">
                      {item.label ?? '—'}
                      {item.fromCurrentForm && !section.fromCurrentForm && (
                        <span className="ml-2 text-xs text-fieldiq-muted print:text-gray-500">{LABEL_FROM_CURRENT_FORM}</span>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <ResultBadge result={item.result} />
                      <ResultIcon result={item.result} />
                    </div>
                  </div>
                  {item.comment && (
                    <p className="text-xs text-fieldiq-muted print:text-gray-500 mt-1.5 pl-1">{item.comment}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Deficiencies — the finding of the inspection.
            GATED ON shows_deficiencies, NOT ON deficiencies.length. A form type
            that captures a defect list and recorded none prints "None recorded";
            one that never asked prints no section at all. Collapsing those two
            into an absent section is how "tool inspected, nothing found" and
            "this form has no such field" became indistinguishable — and the
            list itself was silently discarded for as long as it did.
            Section order matches the PDF: checklist, deficiencies, notes. */}
        {showsDeficiencies && (
          <div className="bg-fieldiq-card print:bg-transparent rounded-xl print:rounded-none p-4 print:px-0 print:border-b print:border-gray-200">
            <h3 className="font-semibold mb-3 text-white print:text-black text-base">Deficiencies</h3>
            {deficiencies.length === 0 ? (
              <p className="text-sm text-fieldiq-muted print:text-gray-500">None recorded</p>
            ) : (
              <div className="space-y-2 print:space-y-1">
                {deficiencies.map((d: any, idx: number) => (
                  <div
                    key={idx}
                    className="bg-fieldiq-dark print:bg-transparent rounded-lg print:rounded-none p-3 print:py-1.5 print:px-0 print:border-b print:border-gray-100 flex items-start justify-between gap-3"
                  >
                    <span className="text-sm text-white print:text-black">{d?.description || '—'}</span>
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-semibold shrink-0 print:bg-transparent print:px-0 print:text-[11px] ${severityStyle(d?.severity)}`}
                    >
                      {severityLabel(d?.severity)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {submission.notes && (
          <div className="bg-fieldiq-card print:bg-transparent rounded-xl print:rounded-none p-4 print:px-0 print:border-b print:border-gray-200">
            <h3 className="font-semibold mb-3 text-white print:text-black text-base">Notes</h3>
            <p className="text-sm text-fieldiq-text print:text-black whitespace-pre-wrap">{submission.notes}</p>
          </div>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <div className="bg-fieldiq-card print:bg-transparent rounded-xl print:rounded-none p-4 print:px-0 print:border-b print:border-gray-200">
            <h3 className="font-semibold mb-3 text-white print:text-black text-base flex items-center gap-2">
              <Camera size={16} className="text-fieldiq-accent print:hidden" />
              Photos ({photos.length})
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 print:grid-cols-4 print:gap-2">
              {photos.map((photo: any) => (
                <button
                  key={photo.id || photo.key}
                  onClick={() => setLightboxUrl(photo.url || photo.presigned_url)}
                  className="aspect-square rounded-lg overflow-hidden border border-fieldiq-border hover:border-fieldiq-accent transition-colors no-print"
                >
                  <img
                    src={photo.url || photo.presigned_url}
                    alt={photo.original_name || 'Inspection photo'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
              {/* Print-only: show images inline without button wrapper */}
              {photos.map((photo: any) => (
                <div
                  key={`print-${photo.id || photo.key}`}
                  className="hidden print:block aspect-square rounded overflow-hidden border border-gray-200"
                >
                  <img
                    src={photo.url || photo.presigned_url}
                    alt={photo.original_name || 'Inspection photo'}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signatures. Both columns may hold inline base64 or an R2 key — see
            resolveSignature. The supervisor block is scoped per form type: only
            the Elevators form captures a second signature, so an elevators
            record shows it empty when nobody countersigned, and every other form
            type shows nothing at all. */}
        {/* `inspector_name`, not the header's inspector-or-operator fallback —
            the PDF's block prints inspector_name alone, and these two must not
            disagree about who signed. */}
        <SignatureBlock
          title="Inspector Signature"
          role="Inspector"
          name={submission.inspector_name || '—'}
          columnValue={submission.inspector_signature}
          signatureUploads={signatureUploads}
          uploadsLoading={signaturesLoading}
        />

        {showsSupervisorSignature && (
          <SignatureBlock
            title="Supervisor Signature"
            role="Supervisor"
            name={submission.supervisor || '—'}
            columnValue={submission.supervisor_signature}
            signatureUploads={signatureUploads}
            uploadsLoading={signaturesLoading}
          />
        )}

        {/* Overall Result */}
        <div className="bg-fieldiq-card print:bg-transparent rounded-xl print:rounded-none p-4 print:px-0">
          <h3 className="font-semibold mb-3 text-white print:text-black text-base">Overall Result</h3>
          <ResultBadge result={submission.overall_result} />
        </div>
      </div>

      {canLogRepair && repairOpen && (
        <Suspense fallback={null}>
          <MaintenanceRecordDialog
            open
            onClose={() => setRepairOpen(false)}
            preselectedEquipmentId={submission.equipment_id}
            inspectionSubmissionId={submission.id}
          />
        </Suspense>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 no-print"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 text-white hover:text-fieldiq-accent transition-colors"
          >
            <X size={28} />
          </button>
          <img
            src={lightboxUrl}
            alt="Inspection photo"
            className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
